import { RequirePermissionPage } from "@components/common/require-permission-page";
import { useAppNavigation } from "@router/use-app-navigation";
import { CreatePlanContainer } from "@pages/create-plan/create-plan-container/create-plan-container";
import { ContentLayout } from "@pages/content-layout";
import { LoadingScreen } from "@components/loading-screen";
import { useAppParams } from "@router/router-helper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect } from "react";
import { setPlanSubject } from "@store/plan-creation/plan-creation-slice";
import { fetchProject } from "@store/project/project-thunks";
import { projectStateSelector } from "@store/project/project-slice";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { StringHeader } from "@components/string-header";
import { isCompanyContext } from "@utils/type-guards";
import { PlanCreationMode } from "@custom-types/base-types";

/** Hardware licenses are always assigned to a workspace, so a project never offers them */
type ProjectPlanCreationMode = Exclude<PlanCreationMode, "hardware-license">;

/** The page title shown for each kind of product the user can add to a project */
const PAGE_TITLES: Record<ProjectPlanCreationMode, string> = {
  subscription: "Create New Project Plan",
  feature: "Add Feature to Project",
};

interface Props {
  /** Which kind of product the user is about to add */
  creationMode: ProjectPlanCreationMode;
}

export function CreateProjectPlanPage({
  creationMode,
}: Props): React.JSX.Element {
  const { projectId, planId } = useAppParams();
  const dispatch = useAppDispatch();
  const { fetchingProject, project } = useAppSelector(projectStateSelector);
  const { handleErrorWithPage } = useErrorHandler();
  const { navigateToProfilePage } = useAppNavigation();

  // Fetches the project
  useEffect(() => {
    async function fetchProjectData(): Promise<void> {
      try {
        await dispatch(fetchProject({ projectId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `CreateProjectPlanPage-${Date.now().toString()}`,
          title: "Could not load Project",
          error,
        });
      }
    }

    void fetchProjectData();
  }, [dispatch, projectId, handleErrorWithPage]);

  // Set the plan subject in the store for plan creation
  useEffect(() => {
    if (project && isCompanyContext(project.context)) {
      dispatch(
        setPlanSubject({
          planSubjectId: project.id,
          planSubjectType: "PROJECT",
          planSubjectName: project.name,
          planSubscriberId: project.context.company.id,
        })
      );
    }
  }, [dispatch, project]);

  return (
    <RequirePermissionPage
      permission="project.addPlan"
      leaveText="Go to project"
      onLeave={() => {
        if (projectId) {
          navigateToProfilePage({
            subjectType: "PROJECT",
            subjectId: projectId,
          });
        }
      }}
    >
      <LoadingScreen
        isFetching={!project || fetchingProject.selectedProject === "pending"}
      >
        {project && (
          <ContentLayout
            header={
              <StringHeader
                title={planId ? "Copy Project Plan" : PAGE_TITLES[creationMode]}
              />
            }
          >
            <CreatePlanContainer creationMode={creationMode} />
          </ContentLayout>
        )}
      </LoadingScreen>
    </RequirePermissionPage>
  );
}
